import { useI18n } from "vue-i18n";

/**
 * Interface mínima para uma pessoa que pode ser formatada
 */
export interface PersonLike {
	firstName?: string | null;
	lastName?: string | null;
	nickname?: string | null;
}

/**
 * Composable para formatação consistente de dados de pessoa
 * Centraliza lógica duplicada em múltiplos componentes
 */
export function usePersonFormatting() {
	const { t } = useI18n();

	/**
	 * Retorna as iniciais de uma pessoa (primeira letra do primeiro e último nome)
	 */
	function getPersonInitials(person: PersonLike | null | undefined): string {
		if (!person) return "?";
		const firstName = person.firstName?.charAt(0) || "";
		const lastName = person.lastName?.charAt(0) || "";
		return (firstName + lastName).toUpperCase() || "?";
	}

	/**
	 * Retorna o nome completo de uma pessoa
	 */
	function getPersonFullName(person: PersonLike | null | undefined): string {
		if (!person) return t("common.unknown");
		const fullName = `${person.firstName || ""} ${person.lastName || ""}`.trim();
		return fullName || t("common.unnamed");
	}

	/**
	 * Retorna o nome de exibição preferencial (apelido ou nome completo)
	 */
	function getPersonDisplayName(person: PersonLike | null | undefined): string {
		if (!person) return t("common.unknown");
		if (person.nickname) return person.nickname;
		return getPersonFullName(person);
	}

	/**
	 * Gera uma cor de avatar baseada no nome da pessoa
	 * Retorna uma cor consistente para o mesmo nome
	 */
	function getPersonAvatarColor(person: PersonLike | null | undefined): string {
		const name = getPersonFullName(person);
		// Gera um hash simples do nome para selecionar uma cor
		let hash = 0;
		for (let i = 0; i < name.length; i++) {
			hash = name.charCodeAt(i) + ((hash << 5) - hash);
		}

		const colors = [
			"primary",
			"secondary",
			"success",
			"info",
			"warning",
			"error",
			"indigo",
			"teal",
			"purple",
			"deep-orange",
		];

		return colors[Math.abs(hash) % colors.length];
	}

	/**
	 * Formata nome com apelido entre parênteses (se existir)
	 */
	function getPersonFullNameWithNickname(
		person: PersonLike | null | undefined
	): string {
		if (!person) return t("common.unknown");
		const fullName = getPersonFullName(person);
		if (person.nickname) {
			return `${fullName} (${person.nickname})`;
		}
		return fullName;
	}

	return {
		getPersonInitials,
		getPersonFullName,
		getPersonDisplayName,
		getPersonAvatarColor,
		getPersonFullNameWithNickname,
	};
}
